import React, { useState } from 'react';
import { Rule, Milestone } from 'types/dt-graphql-apollo-v2';
import { Button, IconType, ButtonProps } from '@cognite/cogs.js';
import { notification } from 'antd';
import {
  AttributesType,
  JobType,
  useStartValidationJob,
  ValidationJobOptions,
} from 'hooks/useStartValidationJob';
import { RunAttributesSelection } from './RunAttributesSelection';

export type RunRulesButtonProps = {
  buttonIcon?: IconType | undefined;
  buttonIconPlacement?: ButtonProps['iconPlacement'];
  // ButtonSize supported in COGS v9
  buttonSize?: ButtonProps['size'];
  buttonText?: string;
  // ButtonType missing in COGS v9 → using supported string union
  buttonType?: ButtonProps['type'];
  entityId: string;
  milestone?: Milestone;
  rules?: Rule[]; // | Ruleset['rules'] | Milestone['rules'];
  schemaCollectionRefId?: string | null;
  type: JobType;
};

export const RunRulesButton = ({
  buttonIcon = undefined,
  buttonIconPlacement = 'left',
  buttonSize = 'medium',
  buttonText = 'Run rules',
  buttonType = 'secondary',
  entityId,
  milestone,
  rules,
  schemaCollectionRefId,
  type,
}: RunRulesButtonProps) => {
  const { startValidationJob, validationJobRunning } = useStartValidationJob();
  const [showAttributesSelection, setShowAttributesSelection] = useState(false);

  const isDisabled = !entityId || validationJobRunning || rules?.length === 0;

  const handleJobSelection = () => {
    if (type === JobType.COLLECTION) {
      setShowAttributesSelection(true);
    } else {
      handleValidationJob();
    }
  };

  const handleValidationJob = async (reportData?: AttributesType) => {
    if (isDisabled) return;

    const options: ValidationJobOptions = {
      attributes: reportData,
      entityId,
      onError: (err) => {
        notification.error({
          message: 'Could not start the data validation job',
          description: err.toString(),
        });
      },
      onSuccess: () => {
        notification.success({
          message: 'In progress',
          description: `Data validation is now in process. This might take a couple of minutes!`,
        });
      },
      schemaCollectionRefId,
      type,
    };

    await startValidationJob(options);
    setShowAttributesSelection(false);
  };

  return (
    <>
      <Button
        aria-label="Start a Data Validation job on the current entity."
        disabled={isDisabled}
        icon={buttonIcon}
        iconPlacement={buttonIconPlacement}
        loading={validationJobRunning}
        onClick={handleJobSelection}
        type={buttonType}
        size={buttonSize}
        style={{ justifyContent: 'flex-start', gap: '0.2rem' }}
      >
        {buttonText}
      </Button>

      {showAttributesSelection && (
        <RunAttributesSelection
          disabled={isDisabled}
          loading={validationJobRunning}
          milestone={milestone}
          onCancel={() => setShowAttributesSelection(false)}
          onCreateReport={handleValidationJob}
          rules={rules as Rule[]}
          schemaCollectionRefId={schemaCollectionRefId}
          visible={showAttributesSelection}
        />
      )}
    </>
  );
};
